"use client";

import { cn } from "@/lib/utils";
import { useModal } from "@/providers/modal-provider";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import Youtube from "@tiptap/extension-youtube";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React, { Ref, useEffect, useImperativeHandle } from "react";
import EditorOptionsMenu from "./components/edit-options-menu";
import { mentionSuggestionOptions } from "./components/suggestion-options";
import "./editor.css";
import { MentionProp } from "@/types/post";

type EditorProps = {
	content: string;
	disable?: boolean;
	placeholder?: string;
	disableOptions?: string[];
	// TODO (ruthvik): Fix types
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	editorRef?: Ref<any> | null;
	classNames?: { editor?: string };
	handleOnUpdate: ({ content, mentions }: { content: string; mentions?: MentionProp[] }) => void;
	header?: React.ReactNode;
	footer?: React.ReactNode;
};

// TODO (ruthvik): Fix hardbreak issue in the editor
// const CustomHardBreak = HardBreak.extend({
// 	addKeyboardShortcuts() {
// 		return {
// 			Enter: () => {
// 				return this.editor.commands.setHardBreak();
// 			},
// 		};
// 	},
// });

const Editor = ({
	classNames,
	placeholder,
	content,
	editorRef,
	disable,
	disableOptions,
	header,
	footer,
	handleOnUpdate,
}: EditorProps) => {
	const { openAuthModal } = useModal();

	const extensions = [
		StarterKit.configure({
			heading: {
				HTMLAttributes: {
					class: "content-heading",
				},
				levels: [1, 2, 3],
			},
			bulletList: {
				keepMarks: true,
				keepAttributes: false,
			},
			orderedList: {
				keepMarks: true,
				keepAttributes: false,
			},
		}),
		// CustomHardBreak,
		Link.configure({
			HTMLAttributes: {
				class: "content-link",
			},
			openOnClick: false,
			autolink: true,
		}),
		Youtube.configure({
			HTMLAttributes: {
				class: "w-full aspect-video content-youtube",
			},
			inline: false,
			origin: "www.youtube.com",
		}),
		Placeholder.configure({
			placeholder: placeholder || "Write something",
		}),
		Mention.configure({
			renderHTML({ options, node }) {
				return [
					"a",
					{ href: node.attrs.id ? `/${node.attrs.id}` : `#`, class: "content-mention" },
					`${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`,
				];
			},
			suggestion: mentionSuggestionOptions,
		}),
		Image.configure({
			HTMLAttributes: {
				class: "content-image",
			},
		}),
	];

	const editor = useEditor({
		extensions,
		content: content,
		onUpdate: ({ editor }) => {
			const html = editor.getHTML();
			const json = editor.getJSON();
			const mentions: MentionProp[] = [];

			json?.content?.map((node) => {
				node?.content?.forEach((object) => {
					if (object.type === "mention") {
						const mention = {
							username: object.attrs?.id,
							name: object.attrs?.label,
						};

						mentions.push(mention);
					}
				});
			});

			handleOnUpdate({ content: html, mentions });
		},
		editorProps: {
			attributes: {
				class: cn(
					"[&_ol]:list-decimal [&_ol]:pl-3 [&_ul]:list-disc [&_ul]:pl-3 min-h-[64px]",
					classNames?.editor
				),
			},
		},
	});

	const clearEditorContent = () => {
		editor?.chain().clearContent().run();
	};

	useEffect(() => {
		if (disable) {
			editor?.setEditable(false);
		} else {
			editor?.setEditable(true);
		}
	}, [editor, disable]);

	useImperativeHandle(editorRef, () => ({
		clearEditorContent: clearEditorContent,
	}));

	return (
		<div className="w-full">
			<div className="mb-2 flex items-center justify-start gap-2">
				{header}

				<EditorOptionsMenu editor={editor} disableOptions={disableOptions} />
			</div>

			{disable && (
				<button
					type="button"
					onClick={() => openAuthModal()}
					className="my-4 text-sm text-gray-400"
				>
					Please login to continue
				</button>
			)}

			<EditorContent ref={editorRef} editor={editor} />

			{footer}
		</div>
	);
};

export default Editor;
